import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addToCart,
  removeFromCart,
  deleteCart,
  reset,
} from "../features/cart/cartSlice";
import { addAddress, removeAddress, addCard } from "../features/auth/authSlice";
import { useNavigate } from "react-router-dom";
import { useToast } from "../components/Toast";
import Loader from "../components/Loader";
import Modal from "../components/Modal";
import { RiDeleteBinLine, RiErrorWarningLine } from "react-icons/ri";
import { IoLocation } from "react-icons/io5";
import { FaCcVisa, FaCcMastercard, FaCcAmex, FaCoins } from "react-icons/fa";
import { HiArrowRight, HiOutlineCreditCard } from "react-icons/hi";
import { getShippingFee } from "../features/shipping/shippingSlice";

const CartModal = ({ cart }) => {
  return (
    <div className="relative w-full">
      {/* Top fade gradient - dark theme */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-[#0b0e14] to-transparent z-10 pointer-events-none" />

      {/* Scrollable container with cyber scrollbar */}
      <div
        className="flex flex-col gap-4 items-center h-[400px] overflow-y-auto w-full px-2 py-4
          scroll-smooth
          [&::-webkit-scrollbar]:w-1.5
          [&::-webkit-scrollbar]:rounded-full
          [&::-webkit-scrollbar-track]:bg-white/5
          [&::-webkit-scrollbar-track]:rounded-full
          [&::-webkit-scrollbar-track]:my-2
          [&::-webkit-scrollbar-thumb]:bg-gradient-to-b
          [&::-webkit-scrollbar-thumb]:from-cyan-500
          [&::-webkit-scrollbar-thumb]:to-purple-600
          [&::-webkit-scrollbar-thumb]:rounded-full
          hover:[&::-webkit-scrollbar-thumb]:from-cyan-400
          hover:[&::-webkit-scrollbar-thumb]:to-purple-500"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#06b6d4 rgba(255,255,255,0.05)",
        }}
      >
        {Array.from(new Set(cart.map((item) => item.code))).map((code) => {
          const product = cart.find((item) => item.code === code);
          return <CartProduct key={code} product={product} cart={cart} />;
        })}
      </div>

      {/* Bottom fade gradient - dark theme */}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#0b0e14] to-transparent z-10 pointer-events-none" />
    </div>
  );
};

const CartProduct = ({ product, cart }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const [isCurrentlyAdding, setIsCurrentlyAdding] = useState(false);
  const [isCurrentlyRemoving, setIsCurrentlyRemoving] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [foundItems, setFoundItems] = useState([]);

  useEffect(() => {
    setFoundItems(cart.filter((item) => item.code === product.code));
  }, [cart, product]);

  useEffect(() => {
    setQuantity(foundItems?.length || 0);
  }, [foundItems]);

  const handleIncrement = () => {
    if (!user || !user.data?.email) {
      toast.info("Please log in to add items to your cart.");
      navigate("/login");
      return;
    }
    setIsCurrentlyAdding(true);
    dispatch(addToCart({ email: user.data.email, item: product }))
      .unwrap()
      .finally(() => {
        setIsCurrentlyAdding(false);
      });
    dispatch(reset());
  };

  const handleDecrement = () => {
    if (!user || !user.data?.email) {
      toast.info("Please log in to update your cart.");
      navigate("/login");
      return;
    }
    setIsCurrentlyRemoving(true);
    dispatch(removeFromCart({ code: product.code, email: user.data.email }))
      .unwrap()
      .finally(() => {
        setIsCurrentlyRemoving(false);
      });
    dispatch(reset());
  };
  return (
    <div className="w-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 hover:border-cyan-500/30 transition-all duration-300 group">
      <div className="flex gap-4">
        {/* Product Image */}
        <div className="w-20 h-20 bg-white/5 border border-white/10 rounded-xl overflow-hidden flex-shrink-0 group-hover:border-cyan-500/30 transition-colors">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain p-2 grayscale group-hover:grayscale-0 transition-all duration-500"
          />
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-black text-white uppercase italic tracking-tight truncate mb-1 group-hover:text-cyan-400 transition-colors">
            {product.name}
          </h4>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">
            {product.category || "HARDWARE"} | {product.code || "ALPHA_GEN"}
          </p>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">
              ${Number(product.price).toFixed(2)}
            </span>
            <span className="text-[8px] text-gray-600 uppercase tracking-widest">
              per unit
            </span>
          </div>
        </div>

        {/* Quantity Controls & Total */}
        <div className="flex flex-col items-end justify-between">
          {/* Quantity Stepper */}
          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-xl p-1">
            <button
              onClick={handleDecrement}
              disabled={isCurrentlyRemoving || quantity === 0}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-lg font-black"
            >
              {isCurrentlyRemoving ? <Loader size="sm" /> : "−"}
            </button>
            <span className="w-8 text-center font-black text-white text-sm">
              {quantity}
            </span>
            <button
              onClick={handleIncrement}
              disabled={isCurrentlyAdding}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-lg font-black"
            >
              {isCurrentlyAdding ? <Loader size="sm" /> : "+"}
            </button>
          </div>

          {/* Line Total */}
          <div className="text-right mt-2">
            <p className="text-[8px] font-bold text-gray-600 uppercase tracking-widest">
              Line Total
            </p>
            <p className="text-lg font-black text-cyan-400 italic tracking-tighter">
              ${(product.price * quantity).toFixed(2)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

function Cart() {
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [checkCard, setCheckCard] = useState({
    visa: false,
    mastercard: false,
    amex: false,
  });
  const [useCredit, setUseCredit] = useState(false);

  // Modal States
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);

  // Form States
  const [addressForm, setAddressForm] = useState({
    address: "",
    city: "",
    state: "",
  });
  const [cardForm, setCardForm] = useState({
    name: "",
    number: "",
    expiry: "",
    cvv: "",
  });

  const handleAddressSubmit = (e) => {
    e.preventDefault();
    if (addressForm.address && addressForm.city && addressForm.state) {
      dispatch(addAddress({ email, address: addressForm }));
      setIsAddressModalOpen(false);
      setAddressForm({ address: "", city: "", state: "" });
    }
  };

  const handleCardSubmit = (e) => {
    e.preventDefault();
    if (cardForm.name) {
      // Mock card type detection
      dispatch(addCard({ email, card: { ...cardForm, type: "visa" } }));
      setIsCardModalOpen(false);
      setCardForm({ name: "", number: "", expiry: "", cvv: "" });
    }
  };

  const { user } = useSelector((state) => state.auth);
  const { isLoading } = useSelector((state) => state.cart);
  const cartItems = user?.data?.cart || [];
  const { shippingFee, isLoading: isShippingLoading } = useSelector(
    (state) => state.shippingFee
  );
  const creditBalance = user?.data?.balance || 0;

  const email = user?.data?.email;
  const shipCost = shippingFee?.data?.shippingFee;

  const handleDeleteCart = () => {
    dispatch(deleteCart({ email }))
      .unwrap()
      .then(() => {
        toast.success("Cart cleared successfully!", { autoClose: 2000 });
      })
      .catch(() => {
        toast.error("Failed to clear cart.", { autoClose: 2000 });
      });
  };

  useEffect(() => {
    if (!user || !user.data?.email) {
      toast.info("Please log in to add items to your cart.");
      navigate("/login");
      return;
    }
  }, [user, navigate, toast]);

  useEffect(() => {
    // Only fetch shipping fee if there are items in the cart
    if (cartItems.length > 0) {
      // Use the first saved address state if available, otherwise default to a reasonable fallback
      const destinationState =
        user?.data?.addresses?.[0]?.state || user?.data?.state || "lagos";

      dispatch(
        getShippingFee({
          originState: "ogun",
          destinationState: destinationState,
          itemsCount: cartItems.length,
        })
      );
    }
  }, [cartItems.length, dispatch, user?.data?.addresses, user?.data?.state]);

  return (
    <div className="min-h-screen bg-[#0b0e14] py-20 px-4 md:px-8 relative overflow-hidden">
      {/* Background Glows */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10 w-full">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-[10px] font-black text-cyan-400 uppercase tracking-widest">
              <span className="w-1 h-1 bg-cyan-400 rounded-full animate-ping" />
              Logistics Terminal
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white uppercase italic tracking-tighter">
              Deployment <span className="text-cyan-400">Hub</span>
            </h1>
          </div>
          <div className="hidden md:flex flex-col items-end gap-1">
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
              System Status
            </p>
            <p className="text-sm font-bold text-green-500 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              ALL_SYSTEMS_OPTIMAL
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Main Content Area */}
          <div className="lg:col-span-8 flex flex-col gap-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Delivery Address Card */}
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-[2rem] group hover:border-cyan-500/30 transition-all duration-500">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-cyan-500/10 border border-cyan-500/20 rounded-2xl flex items-center justify-center text-cyan-400">
                      <IoLocation className="text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white uppercase tracking-tight">
                        Drop Point
                      </h3>
                      <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                        Address Matrix
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsAddressModalOpen(true)}
                    className="p-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-colors text-cyan-400"
                  >
                    <HiArrowRight className="-rotate-45" />
                  </button>
                </div>

                <div className="space-y-3 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                  {user?.data?.addresses?.map((addr) => (
                    <div
                      key={addr.id}
                      className="relative bg-white/5 border border-white/5 p-4 rounded-2xl hover:bg-white/[0.08] transition-all group/item"
                    >
                      <p className="text-sm font-bold text-white mb-0.5">
                        {addr.address}
                      </p>
                      <p className="text-[10px] font-black text-gray-500 uppercase tracking-wider">
                        {addr.city}, {addr.state}
                      </p>
                      <button
                        onClick={() =>
                          dispatch(removeAddress({ email, id: addr.id }))
                        }
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-gray-600 hover:text-red-500 opacity-0 group-hover/item:opacity-100 transition-opacity"
                      >
                        <RiDeleteBinLine />
                      </button>
                    </div>
                  ))}
                  {(!user?.data?.addresses ||
                    user?.data?.addresses.length === 0) && (
                    <div className="py-8 text-center border-2 border-dashed border-white/5 rounded-2xl">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                        No address detected
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Method Card */}
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-[2rem] group hover:border-purple-500/30 transition-all duration-500">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500/10 border border-purple-500/20 rounded-2xl flex items-center justify-center text-purple-400">
                      <HiOutlineCreditCard className="text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white uppercase tracking-tight">
                        Loot Access
                      </h3>
                      <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                        Payment Matrix
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsCardModalOpen(true)}
                    className="p-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-colors text-purple-400"
                  >
                    <HiArrowRight className="-rotate-45" />
                  </button>
                </div>

                <div className="space-y-3 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                  {user?.data?.paymentMethods?.map((card) => (
                    <label
                      key={card.id}
                      className={`block relative bg-white/5 border p-4 rounded-2xl cursor-pointer transition-all ${
                        checkCard[card.id]
                          ? "border-purple-500/50 bg-purple-500/5"
                          : "border-white/5 hover:bg-white/[0.08]"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-white">
                            {card.type === "visa" ? (
                              <FaCcVisa className="text-2xl" />
                            ) : (
                              <FaCcMastercard className="text-2xl" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">
                              •••• {card.last4 || card.number?.slice(-4)}
                            </p>
                            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                              {card.name}
                            </p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={checkCard[card.id]}
                          onChange={() => setCheckCard({ [card.id]: true })}
                        />
                        <div
                          className={`w-4 h-4 rounded-full border-2 transition-colors ${
                            checkCard[card.id]
                              ? "border-purple-500 bg-purple-500"
                              : "border-white/20"
                          }`}
                        />
                      </div>
                    </label>
                  ))}
                  {(!user?.data?.paymentMethods ||
                    user?.data?.paymentMethods.length === 0) && (
                    <div className="py-8 text-center border-2 border-dashed border-white/5 rounded-2xl">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                        No keys detected
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Cart Items List */}
            <div className="bg-white/5 rounded-[2.5rem] p-8 border border-white/10 shadow-2xl relative">
              <div className="absolute top-0 right-10 -translate-y-1/2 px-4 py-1 bg-[#0b0e14] border border-white/10 rounded-full">
                <p className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">
                  Active Loadout: {cartItems.length} SLOTS
                </p>
              </div>

              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-black text-white uppercase italic tracking-tighter">
                  Current <span className="text-cyan-400">Inventory</span>
                </h2>
                {cartItems.length > 0 && (
                  <button
                    onClick={handleDeleteCart}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-xl transition-all font-black uppercase text-[10px] tracking-widest border border-red-500/20"
                  >
                    {isLoading ? (
                      <Loader size="sm" />
                    ) : (
                      <>
                        <RiDeleteBinLine /> Purge Inventory
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="min-h-[300px]">
                {cartItems.length > 0 ? (
                  <CartModal cart={cartItems} />
                ) : (
                  <div className="flex flex-col items-center justify-center py-20 text-center opacity-40">
                    <RiErrorWarningLine className="text-6xl mb-4" />
                    <p className="text-xl font-bold uppercase tracking-tight text-white italic">
                      Your arsenal is empty
                    </p>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mt-2">
                      Recruitment required
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Checkout Summary Area */}
          <div className="lg:col-span-4 flex flex-col gap-6 sticky top-28">
            {/* Credit Status Card */}
            <div className="bg-gradient-to-br from-amber-500/20 to-yellow-600/10 border border-amber-500/20 p-6 rounded-3xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full -translate-y-16 translate-x-16 blur-3xl group-hover:bg-amber-500/10 transition-all duration-700" />
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-2xl flex items-center justify-center text-amber-500">
                    <FaCoins className="text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-white uppercase tracking-tight">
                      Gamer Pts
                    </h3>
                    <p className="text-2xl font-black text-amber-500 tracking-tighter">
                      {creditBalance.toLocaleString()}
                    </p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  className="w-6 h-6 bg-white/5 border border-amber-500/20 rounded-lg accent-amber-500 cursor-pointer"
                  checked={useCredit}
                  onChange={(e) => setUseCredit(e.target.checked)}
                />
              </div>
              {useCredit && (
                <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                  <p className="text-[10px] font-black text-amber-500 uppercase tracking-widest leading-relaxed text-center">
                    Power-Up: Credits Activated
                  </p>
                </div>
              )}
            </div>

            {/* Total Calculation Card */}
            <div className="bg-white/5 backdrop-blur-3xl border border-white/10 p-8 rounded-[2.5rem] shadow-2xl space-y-6">
              <h3 className="text-xl font-black text-white uppercase tracking-widest mb-6 border-b border-white/5 pb-4">
                Order Metadata
              </h3>

              <div className="space-y-4">
                <div className="flex justify-between items-center opacity-60">
                  <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
                    Subtotal
                  </span>
                  <span className="text-sm font-black text-white">
                    $
                    {cartItems
                      ?.reduce((acc, item) => acc + Number(item.price), 0)
                      .toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold uppercase tracking-widest text-gray-400 opacity-60">
                    Logistics
                  </span>
                  <span className="text-sm font-black text-green-400 uppercase tracking-wider">
                    {isShippingLoading ? (
                      <Loader size="sm" />
                    ) : (
                      `$${shipCost || 0}`
                    )}
                  </span>
                </div>
                <div className="pt-6 border-t border-white/10 mt-6">
                  <div className="flex justify-between items-end mb-8">
                    <div className="space-y-1">
                      <p className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">
                        Deployment Total
                      </p>
                      <h4 className="text-4xl font-black text-white italic tracking-tighter uppercase">
                        Total
                      </h4>
                    </div>
                    <div className="text-right">
                      <p className="text-4xl font-black text-cyan-400 tracking-tighter">
                        $
                        {cartItems && cartItems.length > 0
                          ? (
                              cartItems?.reduce(
                                (acc, item) => acc + Number(item.price),
                                0
                              ) + (shipCost || 0)
                            ).toFixed(2)
                          : "0.00"}
                      </p>
                      <p className="text-[10px] font-black text-gray-600 uppercase tracking-widest">
                        Inc. VAT Override
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      if (user?.data?.addresses?.length > 0) {
                        if (user?.data?.paymentMethods?.length > 0) {
                          if (cartItems?.length > 0) navigate("/checkout");
                          else toast("Inventory Error: Cart Empty");
                        } else setIsCardModalOpen(true);
                      } else setIsAddressModalOpen(true);
                    }}
                    disabled={isShippingLoading || cartItems.length === 0}
                    className="w-full group relative p-6 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-[2rem] text-white font-black uppercase tracking-[0.2em] italic text-sm hover:from-cyan-400 hover:to-purple-500 transition-all duration-300 shadow-2xl shadow-cyan-500/20 active:scale-[0.98] disabled:opacity-30 overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                    <span className="relative flex justify-center items-center gap-3">
                      Initialize Checkout{" "}
                      <HiArrowRight className="text-xl group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>

                  <div className="flex justify-center items-center gap-6 mt-8 opacity-20 grayscale hover:grayscale-0 transition-all">
                    <FaCcVisa className="text-3xl text-white" />
                    <FaCcMastercard className="text-3xl text-white" />
                    <FaCcAmex className="text-3xl text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Address Modal */}
      <Modal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        title="Add New Address"
      >
        <form onSubmit={handleAddressSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              Street Address
            </label>
            <input
              required
              type="text"
              value={addressForm.address}
              onChange={(e) =>
                setAddressForm({ ...addressForm, address: e.target.value })
              }
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="123 Luxury Lane"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                City
              </label>
              <input
                required
                type="text"
                value={addressForm.city}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, city: e.target.value })
                }
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder="New York"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                State
              </label>
              <input
                required
                type="text"
                value={addressForm.state}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, state: e.target.value })
                }
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder="NY"
              />
            </div>
          </div>
          <button
            type="submit"
            className="w-full py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-colors mt-4"
          >
            Save Address
          </button>
        </form>
      </Modal>

      {/* Card Modal */}
      <Modal
        isOpen={isCardModalOpen}
        onClose={() => setIsCardModalOpen(false)}
        title="Add Payment Method"
      >
        <form onSubmit={handleCardSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              Cardholder Name
            </label>
            <input
              required
              type="text"
              value={cardForm.name}
              onChange={(e) =>
                setCardForm({ ...cardForm, name: e.target.value })
              }
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              Card Number
            </label>
            <input
              required
              type="text"
              value={cardForm.number}
              onChange={(e) =>
                setCardForm({ ...cardForm, number: e.target.value })
              }
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="0000 0000 0000 0000"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                Expiry
              </label>
              <input
                required
                type="text"
                value={cardForm.expiry}
                onChange={(e) =>
                  setCardForm({ ...cardForm, expiry: e.target.value })
                }
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder="MM/YY"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                CVV
              </label>
              <input
                required
                type="text"
                value={cardForm.cvv}
                onChange={(e) =>
                  setCardForm({ ...cardForm, cvv: e.target.value })
                }
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder="123"
              />
            </div>
          </div>
          <button
            type="submit"
            className="w-full py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-colors mt-4"
          >
            Save Card
          </button>
        </form>
      </Modal>
    </div>
  );
}

export default Cart;
